import type { ReactNode } from 'react'
import { useNavigate, useRouterState } from '@tanstack/react-router'
import { CircleAlert, Info, TriangleAlert } from 'lucide-react'
import { cx } from '@/lib/clases'
import { Boton } from '@/ui/Boton'
import { BORDE_IZQUIERDO, FONDO_TENUE, type Tono } from '@/ui/tonos'
import type { EstadoCuenta } from '@/dominio/cuenta/tipos'
import { useEstadoCuenta } from '@/dominio/cuenta/useCuenta'

/**
 * El aviso fijo de la cuenta con Agentical Group (`docs/adr/0020`), en todo el portal.
 *
 * Es el caso «bloqueo total» de `ui/Banner`: una franja que no se cierra,
 * encima del contenido, en cada pantalla. **No se puede cerrar a propósito**:
 * un aviso de «el día 18 deja de emitir» que se descarta con una equis se
 * descubre el día 18 con un cliente en la caja.
 *
 * Por vencer va en azul; en gracia, en ámbar; restringida y suspendida, en
 * rojo. Al día no pinta nada.
 */
export function AvisoCuenta() {
  const { data: estado } = useEstadoCuenta()
  const ruta = useRouterState({ select: (e) => e.location.pathname })
  const navegar = useNavigate()

  if (!estado || estado.etapa === 'al_dia') return null

  const tono: Tono = estado.etapa === 'por_vencer' ? 'azul' : estado.etapa === 'en_gracia' ? 'ambar' : 'rojo'
  const Icono = tono === 'azul' ? Info : tono === 'ambar' ? TriangleAlert : CircleAlert
  const enSuscripcion = ruta === '/suscripcion' || ruta.startsWith('/suscripcion/')

  return (
    <div
      role={tono === 'rojo' ? 'alert' : 'status'}
      className={cx(
        'flex shrink-0 items-center gap-3 border-b border-l-[3px] border-gris-300 px-4 py-2 sin-impresion',
        FONDO_TENUE[tono],
        BORDE_IZQUIERDO[tono],
      )}
    >
      <Icono aria-hidden="true" className="size-4 shrink-0 text-gris-700" />
      <p className="min-w-0 flex-1 text-cuerpo text-gris-900">
        <span className="font-semibold">{titulo(estado)}</span> <span className="text-gris-700">{estado.mensaje}</span>
      </p>
      {!enSuscripcion ? (
        <Boton
          tamano="menor"
          variante={tono === 'rojo' ? 'primario' : 'secundario'}
          onClick={() => void navegar({ to: '/suscripcion' })}
        >
          Ver cómo pagar
        </Boton>
      ) : null}
    </div>
  )
}

function titulo(estado: EstadoCuenta): string {
  switch (estado.etapa) {
    case 'por_vencer':
      return estado.causa === 'prueba' ? 'Su prueba termina pronto.' : 'Su suscripción vence pronto.'
    case 'en_gracia':
      return 'Pago de la suscripción vencido.'
    case 'restringido':
      return 'No se emiten comprobantes nuevos.'
    case 'suspendido':
      return 'Cuenta suspendida.'
    default:
      return ''
  }
}

/** Lo que con la cuenta suspendida se deja abrir: pagar y exportar lo emitido. */
const ABIERTAS_SUSPENDIDA = ['/suscripcion', '/documentos', '/recibidos', '/reportes', '/inicio', '/configuracion', '/nomina']

/** Lo que con la cuenta restringida no tiene sentido abrir: crear comprobantes nuevos. */
const CERRADAS_RESTRINGIDA = ['/emitir']

function coincide(ruta: string, prefijos: readonly string[]): boolean {
  return prefijos.some((p) => ruta === p || ruta.startsWith(`${p}/`))
}

/**
 * Lo que la cuenta deja hacer, en pantalla.
 *
 * **Es comodidad, no seguridad**: el servidor niega igual la emisión y las
 * escrituras con los filtros de la cuenta. Aquí se evita que alguien llene una
 * factura entera para recibir un 403 al final. Restringida, se cierra Emitir;
 * suspendida, solo queda lo que sirve para pagar y para exportar lo emitido.
 * La nómina se deja abrir porque el servidor la pone en solo lectura, y sus
 * archivos son exportaciones.
 *
 * Si el estado no cargó, no se cierra nada: el servidor decide al emitir.
 */
export function GuardaCuenta({ children }: { children: ReactNode }) {
  const { data: estado } = useEstadoCuenta()
  const ruta = useRouterState({ select: (e) => e.location.pathname })
  const navegar = useNavigate()

  if (!estado) return <>{children}</>

  const cerrada =
    (estado.etapa === 'restringido' && coincide(ruta, CERRADAS_RESTRINGIDA)) ||
    (estado.etapa === 'suspendido' && ruta !== '/' && !coincide(ruta, ABIERTAS_SUSPENDIDA))

  if (!cerrada) return <>{children}</>

  return (
    <div className="flex max-w-[640px] flex-col gap-3 rounded-md border border-gris-300 bg-white p-6">
      <h1 className="text-pantalla text-gris-900">
        {estado.etapa === 'suspendido' ? 'La cuenta está suspendida' : 'No se emiten comprobantes nuevos'}
      </h1>
      <p className="text-cuerpo text-gris-700">{estado.mensaje}</p>
      <p className="text-cuerpo text-gris-700">
        Los comprobantes ya emitidos se pueden consultar y descargar en Documentos, y los reportes se siguen
        exportando.
      </p>
      <div>
        <Boton variante="primario" onClick={() => void navegar({ to: '/suscripcion' })}>
          Ver cómo pagar
        </Boton>
      </div>
    </div>
  )
}
